import React, { useState, useEffect, useRef } from "react";
import { useAppSelector, useAppDispatch } from "hooks/store";
import api from "config/api";
import { SketchPicker } from "react-color";
import { toast } from "react-toastify";
import { setSettings } from "store/slices/main.slice";
const convertNumberToHex = (num: number): string => {
  if (num === 0) return "#000";
  return `#${num.toString(16).padStart(6, "0")}`;
};

const convertHexToNumber = (hex: string) => parseInt(hex.replace("#", "0x"));
const MAX_FILE_SIZE = 2 * 1024 * 1024;

const CompanyProfile: React.FC = () => {
  const [newLogo, setNewLogo] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const { settings, companyData } = useAppSelector(({ main }) => main);
  const dispatch = useAppDispatch();

  const [themeColor, setThemeColor] = useState("#000");
  const [itemPriceColor, setItemPriceColor] = useState("#000");
  const [itemColor, setItemColor] = useState("#000");
  const [deptColor, setDeptColor] = useState("#000");
  const [themeSelColor, setThemeSelColor] = useState("#000");
  const [headerColor, setHeaderColor] = useState("#000");
  const [itemPriceBgColor, setItemPriceBgColor] = useState("#000");

  const initialThemeColorRef = useRef("#000");
  const initialItemPriceColorRef = useRef("#000");
  const initialItemColorRef = useRef("#000");
  const initialDeptColorRef = useRef("#000");
  const initialThemeSelColorRef = useRef("#000");
  const initialHeaderSelColorRef = useRef("#000");
  const initialItemPriceBGSelColorRef = useRef("#000");

  useEffect(() => {
    setPreviewUrl(companyData?.logoUrl);

    const initialThemeColor = convertNumberToHex(settings.themeColor || 0x000);
    const initialItemPriceColor = convertNumberToHex(
      settings.itemPriceColor || 0x000
    );
    const initialItemColor = convertNumberToHex(settings.itemColor || 0x000);
    const initialDeptColor = convertNumberToHex(settings.deptColor || 0x000);
    const initialThemeSelColor = convertNumberToHex(
      settings.themeSelColor || 0x000
    );

    setThemeColor(initialThemeColor);
    setItemPriceColor(initialItemPriceColor);
    setItemColor(initialItemColor);
    setDeptColor(initialDeptColor);
    setThemeSelColor(initialThemeSelColor);

    initialThemeColorRef.current = initialThemeColor;
    initialItemPriceColorRef.current = initialItemPriceColor;
    initialItemColorRef.current = initialItemColor;
    initialDeptColorRef.current = initialDeptColor;
    initialThemeSelColorRef.current = initialThemeSelColor;
  }, [companyData, settings]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      if (file.size > MAX_FILE_SIZE) {
        toast.error(
          "File size exceeds 2MB limit. Please upload a smaller file."
        );
        return;
      }

      setNewLogo(file);
      const fileUrl = URL.createObjectURL(file);
      setPreviewUrl(fileUrl);
    }
  };

  const uploadLogo = async (): Promise<string | null> => {
    if (!newLogo) return null;

    const formData = new FormData();
    formData.append("formFile", newLogo);

    try {
      const response = await api.post("/api/FileUtility/UploadLogo", formData);
      const responseData = response.data?.data?.data?.[0];

      if (responseData?.status === "OK") {
        toast.success("Logo uploaded successfully");
        return responseData.fileName;
      } else if (responseData?.status === "ERROR") {
        const errorMessage =
          responseData.errorMessage || "Failed to upload logo";
        toast.error(errorMessage);
        return null;
      } else {
        toast.error("Unexpected response from the server");
        return null;
      }
    } catch (error) {
      toast.error("Failed to upload logo due to a network error");
      return null;
    }
  };

  const handleUpdate = async () => {
    setIsLoading(true);

    const logoUrl = await uploadLogo();
    if (!logoUrl && newLogo) {
      setIsLoading(false);
      return;
    }
    const hasThemeColorChanged = themeColor !== initialThemeColorRef.current;
    const hasItemPriceColorChanged =
      itemPriceColor !== initialItemPriceColorRef.current;
    const hasItemColorChanged = itemColor !== initialItemColorRef.current;
    const hasDeptColorChanged = deptColor !== initialDeptColorRef.current;
    const hasThemeSelColorChanged =
      themeSelColor !== initialThemeSelColorRef.current;
    const hasHeaderColorChanged =
      headerColor !== initialHeaderSelColorRef.current;
    const hasItemPriceBGColorChanged =
      itemPriceBgColor !== initialItemPriceBGSelColorRef.current;

    if (
      hasThemeColorChanged ||
      hasItemPriceColorChanged ||
      hasItemColorChanged ||
      hasDeptColorChanged ||
      hasThemeSelColorChanged ||
      hasHeaderColorChanged ||
      hasItemPriceBGColorChanged
    ) {
      api
        .post("/api/Option/AddUpdateSettings", [
          { Id: 0, Value: convertHexToNumber(themeColor), Name: "themeColor" },
          {
            Id: 0,
            Value: convertHexToNumber(itemPriceColor),
            Name: "itemPriceColor",
          },
          { Id: 0, Value: convertHexToNumber(itemColor), Name: "itemColor" },
          { Id: 0, Value: convertHexToNumber(deptColor), Name: "deptColor" },
          {
            Id: 0,
            Value: convertHexToNumber(themeSelColor),
            Name: "themeSelColor",
          },
          {
            Id: 0,
            Value: convertHexToNumber(headerColor),
            Name: "headerColor",
          },
          {
            Id: 0,
            Value: convertHexToNumber(itemPriceBgColor),
            Name: "itemPriceBgColor",
          },
        ])
        .then((res) => {
          dispatch(
            setSettings({
              ...settings,
              themeColor: convertHexToNumber(themeColor),
              itemPriceColor: convertHexToNumber(itemPriceColor),
              itemColor: convertHexToNumber(itemColor),
              deptColor: convertHexToNumber(deptColor),
              themeSelColor: convertHexToNumber(themeSelColor),
              headerColor: convertHexToNumber(headerColor),
              itemPriceBgColor: convertHexToNumber(itemPriceBgColor),
            })
          );
          toast.success("Settings updated successfully");
        })
        .catch((e) => {
          toast.error("Something went wrong");
        })
        .finally(() => setIsLoading(false));
    }
  };

  return (
    <div className="h-full w-full flex flex-col  p-4 overflow-y-auto ">
      <h2 className="text-2xl font-bold text-center text-gray-800">
        Company Profile
      </h2>

      {settings.showLogo !== 0 && (
        <div className="mt-8 flex flex-col items-center">
          {previewUrl && (
            <div className="flex justify-center items-center max-h-[200px] p-1 mb-6">
              <img
                src={previewUrl}
                alt="Company Logo"
                className="max-h-[200px] aspect-square rounded-3xl border-4 p-1 border-gray-200 object-cover"
              />
            </div>
          )}
          <label className="block text-lg font-semibold text-gray-600 mb-2">
            Upload New Logo
          </label>
          <input
            type="file"
            onChange={handleLogoChange}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
            accept="image/*"
          />
        </div>
      )}

      {/* <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-800">
          Customize Colors
        </h3>
        <div className="flex flex-wrap mt-4 gap-8">
          <div className="flex flex-col items-center">
            <label className="block text-lg font-semibold text-gray-600 mb-2">
              Theme Color
            </label>
            <SketchPicker
              color={themeColor}
              onChangeComplete={(color) => setThemeColor(color.hex)}
            />
          </div>

          <div className="flex flex-col items-center">
            <label className="block text-lg font-semibold text-gray-600 mb-2">
              Item Price Color
            </label>
            <SketchPicker
              color={itemPriceColor}
              onChangeComplete={(color) => setItemPriceColor(color.hex)}
            />
          </div>
        </div>
      </div> */}
      {/* 
"headerColor": "",
        "itemPriceBgColor": "", */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-800">
          Customize Colors
        </h3>
        <div className="flex flex-wrap mt-4 gap-8">
          {/* Existing color pickers */}
          {[
            {
              label: "Theme Color",
              color: themeColor,
              setColor: setThemeColor,
            },
            {
              label: "Item Price Color",
              color: itemPriceColor,
              setColor: setItemPriceColor,
            },
            { label: "Item Color", color: itemColor, setColor: setItemColor },
            {
              label: "Department Color",
              color: deptColor,
              setColor: setDeptColor,
            },
            {
              label: "Theme Selection Color",
              color: themeSelColor,
              setColor: setThemeSelColor,
            },
            {
              label: "Header Color",
              color: headerColor,
              setColor: setHeaderColor,
            },
            {
              label: "Item Price Background Color",
              color: itemPriceBgColor,
              setColor: setItemPriceBgColor,
            },
          ].map(({ label, color, setColor }) => (
            <div className="flex flex-col items-center" key={label}>
              <label className="block text-lg font-semibold text-gray-600 mb-2">
                {label}
              </label>
              <SketchPicker
                color={color}
                onChangeComplete={(color) => setColor(color.hex)}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center mt-2 col-span-1 lg:col-span-2">
        <button
          onClick={handleUpdate}
          className="mt-8 w-full sm:w-auto px-6 py-3 bg-primary text-themeSelColor font-semibold rounded-lg shadow  transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading ? "Updating..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default CompanyProfile;
